import React, {useCallback, useEffect, useId, useMemo, useRef} from 'react'
import {Box, Link as ChakraLink, mergeRefs, useSlotRecipe} from '@chakra-ui/react3'
import {Global} from '@emotion/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import FocusLock from 'react-focus-lock'
import useHideOnScroll from '../../../../hooks/use-hide-on-scroll'
import useMeasuredCssVar from '../../../../hooks/use-measured-css-var'
import useNavigation from '../../../../hooks/use-navigation'
import {useNavigationHeaderContent} from '../../../../hooks/useScapiCustomApi'
import {useCurrentCustomer} from '../../../../hooks/use-current-customer'
import {useCommerceData} from '../../../../hooks/use-commerce-data'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import {isAbsoluteUrl, normalizeContentLink} from '../../../../utils/url'
import {ACCOUNT_HREF, HOME_HREF, SIGN_IN_HREF} from '../../../../constants'
import {headerRecipe, HEADER_LINK_VARIANT, HEADER_VIEWPORT_OFFSET_VAR} from './index.recipe'
import {subscribeHeaderVariantChange} from './header-variant'
import {useNavigationState} from '../navigation-provider'
import Flyout, {NavBackdrop} from '../flyout'
import SearchCta from './partials/search-cta'
import {useIsSearchOverlayOpen} from '../search-overlay'
import WishlistCta from './partials/wishlist-cta'
import CartCta from './partials/cart-cta'
import AccountCta from './partials/account-cta'
import HeaderIconButton from './partials/header-icon-button'
import HamburgerIcon from './partials/hamburger-icon'
import {DesktopLLogoIcon} from '../../icons'
import Link from '../../link'
import {NavigationMenu} from '../../navigation-menu'
import RichText from '../../cms/rich-text'
import useCssVarValue from '../../../hooks/useCssVarValue'
import {trackNavigationClick} from '../../../../tracking/analytics'

export const HEADER_FOCUS_LOCK_TESTID = 'header-focus-lock'

// --header-hide-threshold is a registered <length>, so it computes to px. It
// resolves to an empty string wherever the stylesheet has not applied.
const parseHideThreshold = (value) => parseFloat(value) || 0

// Once everything above the sticky header has scrolled off, its top reads 0 or
// less. A negative offset would give the flyout more room than the viewport has.
const measureViewportOffset = (element) => `${Math.max(0, element.getBoundingClientRect().top)}px`

/**
 * Xleap navigation header.
 *
 * Renders a single sticky, full-bleed bar whose inner content is aligned with
 * the page modules via `layerStyle="container.page"` (the `ModuleContainer`
 * pattern). It shows the Stokke logo linking to home, a main link that opens
 * the flyout, and the header navigation links.
 *
 * Navigation data is read from `useNavigationHeaderContent`, which is backed
 * by the `shopper-navigation` `navigation-header` endpoint. Its
 * `headerMainLink.title` labels the "Products" main link that opens the
 * flyout. Its `elements` are the regular header links, rendered with their own
 * `title`/`accentColor` fields as received — only elements carrying both a
 * `title` and a `link.value` become header links; `link.value` is resolved to a
 * relative `to` URL via `normalizeContentLink` (which strips the domain and the
 * site/locale segment before the shared `Link` adds the current site/locale).
 *
 * The bar auto-hides on scroll-down and reveals on scroll-up, driven by
 * `useHideOnScroll` and reflected as a `data-hidden` attribute on the sticky
 * root (the recipe owns the actual slide transform). Its threshold is the end of
 * the header's placement area, read from `--header-hide-threshold`, so the bar
 * travels with the page while that area is on screen. It is disabled — forced
 * shown — while the flyout is open.
 *
 * The CTA cluster composition is site-dependent: the cart CTA is rendered only
 * where commerce is enabled (`useCommerceData`), so content-only sites — which
 * have no basket and no add-to-cart — get a header without a cart entry point.
 *
 * The `<header>` DOM node is exposed via a forwarded ref so the app shell can
 * measure it and resolve `useHeader().headerWrapper` to its scroll-container
 * parent (`#app`).
 *
 * The open-state comes from `<NavigationProvider>`, which the app shell mounts
 * rather than this header: the Zendesk widget picks its layer from that same
 * state, so the provider has to sit above `<ZendeskChatProvider>` as well
 *
 * @component
 * @param {Object} props
 * @param {Function} [props.onAccountClick] - Opens the auth modal for guest shoppers.
 * @returns {JSX.Element} The sticky navigation header.
 */
const HeaderContent = React.forwardRef(({onAccountClick}, ref) => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const navigate = useNavigation()
    const {open, actions} = useNavigationState()
    const isSearchOverlayOpen = useIsSearchOverlayOpen()
    const {myStokkeEnabled} = useOcapiSite().preferences
    const {isEcommerceEnabled} = useCommerceData()
    const {
        data: {isRegistered}
    } = useCurrentCustomer()
    const logout = useAuthHelper(AuthHelpers.Logout)

    // Auto-hide on scroll-down, reveal on scroll-up. Disabled while the flyout
    // is open so the header never slides away mid-interaction.
    //
    // Hiding only starts once the header's placement area has scrolled off the
    // top: within it the header is still where the page put it, so it travels
    // up with the content instead of sliding away (STOK-17403).
    // --header-hide-threshold (app/leap/theme/global-css.js) derives that
    // offset from the header clearance, which already drops the bar's own
    // height for the floating variant, so nothing here has to measure the DOM.
    const [placementAreaEnd, refreshPlacementAreaEnd] = useCssVarValue('--header-hide-threshold', {
        parser: parseHideThreshold,
        initialValue: 0
    })

    // Pages declare the layout variant as CSS on :root (header-variant.js), and
    // the threshold collapses for the floating one. Navigating between an
    // inline and a floating page therefore changes the threshold while the
    // header itself stays mounted and re-renders nothing that would re-read it.
    //
    // The route change alone is too early to sample it, though: a page only
    // declares its variant once its content has loaded, several commits later,
    // rendering a loading skeleton and no variant until then. Reading on the
    // route change therefore picks up the outgoing page's threshold and keeps it
    // for the whole visit (STOK-17477), so re-read on the announcement too.
    useEffect(
        () => subscribeHeaderVariantChange(refreshPlacementAreaEnd),
        [refreshPlacementAreaEnd]
    )

    // The threshold also carries the message-bar term, which has no
    // announcement of its own — a route change is the closest signal, and it can
    // land before the term does.
    useEffect(() => {
        refreshPlacementAreaEnd()
    }, [location.pathname, refreshPlacementAreaEnd])

    const hidden = useHideOnScroll({threshold: placementAreaEnd, disabled: open})

    // While the flyout or the search overlay is open, publish how far this
    // sticky root sits below the top of the viewport, so both are placed against
    // the room actually left below the bar. The recipe can only state the
    // resting value: the message bar scrolls away, that reservation does not
    // (STOK-17548, STOK-17653).
    //
    // The value stays after the close on purpose. The flyout fades out over
    // 400ms, and giving the resting value back mid-fade would paint the strip of
    // backdrop this fixes. The next open measures again before paint.
    //
    // The app shell wants the same `<header>` node, so the two refs are merged.
    const setHeaderElement = useMeasuredCssVar(HEADER_VIEWPORT_OFFSET_VAR, measureViewportOffset, {
        active: open || isSearchOverlayOpen
    })
    const headerRef = useMemo(() => mergeRefs(ref, setHeaderElement), [ref, setHeaderElement])

    const {data} = useNavigationHeaderContent()

    // CMS-provided label for the "Products" main link.
    const mainLinkLabel = data?.headerMainLink?.title

    // Regular header links navigate to a URL. Strip any CMS-provided site/locale
    // segment before render so the shared Link can add the current site/locale.
    const links = useMemo(() => {
        const elements = Array.isArray(data?.elements) ? data.elements : []
        return elements
            .filter((element) => element?.title && element?.link?.value)
            .map((element) => {
                const to = normalizeContentLink(element.link.value)
                return {
                    ...element,
                    to,
                    // `normalizeContentLink` converts internal Stokke URLs to relative paths while
                    // leaving external URLs absolute. Use `isAbsoluteUrl` here instead of
                    // `isExternalLink`, because this code runs during SSR where `window` is unavailable.
                    isExternal: isAbsoluteUrl(to)
                }
            })
    }, [data])

    const recipe = useSlotRecipe({recipe: headerRecipe})
    const styles = useMemo(
        () => recipe({open, searchOpen: isSearchOverlayOpen}),
        [recipe, open, isSearchOverlayOpen]
    )

    // The portaled desktop search overlay anchors to the rendered card, so it
    // inherits the card's actual max width and viewport position.
    const cardRef = useRef()

    // Shared region id ties both triggers' aria-controls to the flyout panel.
    const flyoutId = useId()

    const isSignInPage = location.pathname.endsWith(SIGN_IN_HREF)

    const handleAccountClick = useCallback(() => {
        actions.close()
        if (!isSignInPage) {
            onAccountClick?.()
        }
    }, [actions, isSignInPage, onAccountClick])

    const handleRegisteredAccountClick = useCallback(() => {
        actions.close()
        navigate(ACCOUNT_HREF)
    }, [actions, navigate])

    const handleMainLinkClick = useCallback(() => {
        trackNavigationClick(0, mainLinkLabel)
        actions.toggle()
    }, [actions, mainLinkLabel])

    return (
        <>
            {/* Publish this header's own footprint into the height chain
                (app/leap/theme/global-css.js). */}
            <Global
                styles={{
                    ':root': {'--header-bar-block-size': 'var(--xleap-header-bar-block-size)'}
                }}
            />
            <Box as="header" ref={headerRef} css={styles.root} data-hidden={hidden || undefined}>
                {/* While open, the flyout is modal on both viewports: this trap
                    keeps Tab within the header — so the trigger, the header CTAs
                    and the backdrop stay reachable and the page behind is not.
                    Containment only: `autoFocus`/`returnFocus` are off because
                    the flyout moves focus in on open and back to the trigger on
                    close. The trap is a no-op under jsdom (no layout), so its
                    containment is verified in the browser. */}
                <FocusLock
                    as={Box}
                    // Exception (docs/agents/review-testing.md): react-focus-lock
                    // reports armed/disarmed only through a
                    // `data-focus-lock-disabled` attribute on this wrapper, which
                    // is a layout container with no ARIA role or accessible name
                    // a test could target — data-testid is the only handle on it.
                    lockProps={{css: styles.focusTrap, 'data-testid': HEADER_FOCUS_LOCK_TESTID}}
                    disabled={!open || isSearchOverlayOpen}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- react-focus-lock's own autoFocus prop, not the DOM autofocus attribute; false as the flyout moves focus in itself
                    autoFocus={false}
                    returnFocus={false}
                >
                    <NavBackdrop />

                    <Box css={styles.cardContainer}>
                        <Box ref={cardRef} css={styles.card} layerStyle="container.page">
                            <Box css={styles.bar}>
                                <Box css={styles.barInner}>
                                    <Box css={styles.logoSearch}>
                                        <Link
                                            to={HOME_HREF}
                                            aria-label={formatMessage({
                                                id: 'header.button.assistive_msg.logo',
                                                defaultMessage: 'Stokke homepage'
                                            })}
                                            css={styles.logo}
                                            onClick={() =>
                                                trackNavigationClick(0, 'Stokke homepage')
                                            }
                                        >
                                            <DesktopLLogoIcon css={styles.logoIcon} />
                                        </Link>

                                        <SearchCta
                                            variant="mobile"
                                            isNavigationOpen={open}
                                            onClick={actions.close}
                                            portalContainer={cardRef}
                                        />
                                    </Box>

                                    <Box as="nav" css={styles.nav}>
                                        {/* Pointer/focus on the trigger starts the
                                            navigation fetch before the click lands. The
                                            desktop panel is content-sized, so without
                                            this the first open expands around an empty
                                            box and the content lands after it. */}
                                        <ChakraLink
                                            as="button"
                                            variant="secondary"
                                            size="small"
                                            aria-expanded={open}
                                            aria-controls={flyoutId}
                                            onPointerEnter={actions.prefetch}
                                            onFocus={actions.prefetch}
                                            onClick={handleMainLinkClick}
                                        >
                                            {/* i18n exception: label is CMS-provided navigation copy, already
                                          locale-resolved by the navigation-header endpoint, so it is
                                          intentionally not wrapped in FormattedMessage. */}
                                            <span>{mainLinkLabel}</span>
                                        </ChakraLink>

                                        <Box css={styles.divider} />

                                        {links.map((element) => (
                                            <Link
                                                key={`${element.to}:${element.title}`}
                                                variant={HEADER_LINK_VARIANT[element.accentColor]}
                                                size="small"
                                                to={element.to}
                                                isExternal={element.isExternal}
                                                onClick={() =>
                                                    trackNavigationClick(0, element.title)
                                                }
                                            >
                                                {/* i18n exception: title is CMS-provided navigation copy, already
                                              locale-resolved by the leap-navigation-header content asset, so it is
                                             intentionally not wrapped in FormattedMessage. */}
                                                <RichText css={styles.navLinkText}>
                                                    {element.title}
                                                </RichText>
                                            </Link>
                                        ))}
                                    </Box>

                                    <Box css={styles.ctas}>
                                        {/* On mobile, opening the flyout replaces the search icon
                                        with a search trigger that opens the full search overlay.
                                        Wishlist and account remain available in the CTA cluster. */}
                                        <SearchCta
                                            onClick={actions.close}
                                            portalContainer={cardRef}
                                        />
                                        <WishlistCta css={styles.desktopCta} />
                                        {myStokkeEnabled && isRegistered ? (
                                            <>
                                                <NavigationMenu
                                                    trigger={<AccountCta as="button" />}
                                                    onSignOutClick={() => logout.mutateAsync()}
                                                />

                                                <AccountCta
                                                    as="button"
                                                    css={styles.mobileAccountCta}
                                                    onClick={handleRegisteredAccountClick}
                                                />
                                            </>
                                        ) : (
                                            <AccountCta
                                                as={!isRegistered ? 'button' : undefined}
                                                css={styles.desktopCta}
                                                onClick={
                                                    !isRegistered ? handleAccountClick : undefined
                                                }
                                            />
                                        )}
                                        {/* Content-only sites have no basket and no add-to-cart, so
                                        the cart entry point stays out of the CTA cluster there
                                        (STOK-17444). `isEcommerceEnabled` keeps it available to the
                                        production testing team on those sites, matching the rest of
                                        the commerce surface. */}
                                        {isEcommerceEnabled && <CartCta />}

                                        {/* Mobile-only flyout toggle, grouped with the cart in the
                            CTA cluster to match Figma (mobile shows the cart and the
                            sandwich menu together on the right; the desktop search icon,
                            wishlist and account CTAs are hidden there). Round icon-button chrome via HeaderIconButton
                            with the animated hamburger/X glyph inside; the glyph is
                            presentational — the accessible name stays on this button.
                            Hidden on desktop via the `menuButton` slot, a separate DOM
                            node never conditionally mounted. */}
                                        <HeaderIconButton
                                            css={styles.menuButton}
                                            aria-expanded={open}
                                            aria-controls={flyoutId}
                                            aria-label={
                                                open
                                                    ? formatMessage({
                                                          id: 'header.menu.close',
                                                          defaultMessage: 'Close menu'
                                                      })
                                                    : formatMessage({
                                                          id: 'header.menu.open',
                                                          defaultMessage: 'Open menu'
                                                      })
                                            }
                                            onPointerEnter={actions.prefetch}
                                            onFocus={actions.prefetch}
                                            onClick={actions.toggle}
                                        >
                                            <HamburgerIcon open={open} />
                                        </HeaderIconButton>
                                    </Box>
                                </Box>
                            </Box>

                            <Flyout id={flyoutId} />
                        </Box>
                    </Box>
                </FocusLock>
            </Box>
        </>
    )
})

if (process.env.NODE_ENV !== 'production') {
    HeaderContent.displayName = 'HeaderContent'
}

HeaderContent.propTypes = {
    onAccountClick: PropTypes.func
}

export default HeaderContent
